import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ShareIcon } from 'lucide-react';
import { useScrollFadeIn } from '../hooks/useScrollFadeIn';
interface ResultsSectionProps {
  selectedImages: number[];
  isVisible: boolean;
}
const NEEDS = ['Seen', 'Belong', 'Purpose', 'Loved', 'Express', 'Create', 'Alive', 'Grow', 'Secure', 'Free', 'Wonder', 'Meaning'];
const YEARNINGS_MAP: Record<string, {
  title: string;
  description: string;
}> = {
  Seen: {
    title: 'To Be Seen',
    description: 'You have a deep need to be truly known and acknowledged for who you are — not just what you do.'
  },
  Create: {
    title: 'To Create',
    description: "You're driven to bring something new into the world — ideas, beauty, solutions that didn't exist before."
  },
  Alive: {
    title: 'To Feel Alive',
    description: 'You crave experiences that make you feel fully present, engaged, and electrically connected to life.'
  },
  Belong: {
    title: 'To Belong',
    description: 'You seek deep connection and a sense of home within a community or relationship.'
  },
  Purpose: {
    title: 'To Have Purpose',
    description: 'You are motivated by a desire to contribute to something larger than yourself.'
  },
  Grow: {
    title: 'To Grow',
    description: 'You have an innate drive to evolve, learn, and expand your capabilities.'
  },
  Free: {
    title: 'To Be Free',
    description: 'You value autonomy and the ability to chart your own course without constraint.'
  },
  Secure: {
    title: 'To Feel Secure',
    description: 'You prioritize stability, safety, and a solid foundation from which to operate.'
  },
  Loved: {
    title: 'To Be Loved',
    description: 'You long for deep, unconditional love — to be cherished for exactly who you are.'
  },
  Express: {
    title: 'To Express',
    description: "You need to share what's inside you — your thoughts, feelings, and unique perspective on the world."
  },
  Wonder: {
    title: 'To Wonder',
    description: "You're drawn to awe, curiosity, and the magic of discovering something unexpected."
  },
  Meaning: {
    title: 'To Find Meaning',
    description: "You seek a deeper understanding of your life's purpose and the significance of your experiences."
  }
};
export function ResultsSection({
  selectedImages,
  isVisible
}: ResultsSectionProps) {
  const {
    ref
  } = useScrollFadeIn();
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [shareLabel, setShareLabel] = useState('Share Your Top Yearnings');
  useEffect(() => {
    if (isVisible) {
      setIsAnalyzing(true);
      const timer = setTimeout(() => setIsAnalyzing(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);
  const chartData = useMemo(() => {
    if (!isVisible) return [];
    const data = NEEDS.map(need => ({
      need,
      value: Math.floor(Math.random() * 21) + 60
    }));
    selectedImages.forEach(imgIdx => {
      let boosts: string[] = [];
      switch (imgIdx) {
        case 0:
          boosts = ['Belong', 'Loved', 'Seen'];
          break;
        case 1:
          boosts = ['Purpose', 'Meaning', 'Grow'];
          break;
        case 2:
          boosts = ['Free', 'Alive', 'Wonder'];
          break;
        case 3:
          boosts = ['Grow', 'Create', 'Purpose'];
          break;
        case 4:
          boosts = ['Express', 'Create', 'Seen'];
          break;
        case 5:
          boosts = ['Belong', 'Loved', 'Secure'];
          break;
        case 6:
          boosts = ['Purpose', 'Grow', 'Alive'];
          break;
        case 7:
          boosts = ['Secure', 'Free', 'Meaning'];
          break;
      }
      boosts.forEach(boostNeed => {
        const item = data.find(d => d.need === boostNeed);
        if (item) {
          item.value = Math.min(100, item.value + Math.floor(Math.random() * 11) + 10);
        }
      });
    });
    return data;
  }, [selectedImages, isVisible]);
  const topFour = useMemo(() => {
    if (!chartData.length) return [];
    return [...chartData].sort((a, b) => b.value - a.value).slice(0, 4);
  }, [chartData]);
  const topThreeYearnings = useMemo(() => {
    if (!topFour.length) return [];
    return topFour.slice(0, 3).map(item => {
      const mapped = YEARNINGS_MAP[item.need];
      if (mapped) return mapped;
      return {
        title: `To ${item.need}`,
        description: `Your results indicate a strong drive towards ${item.need.toLowerCase()} in your current life stage.`
      };
    });
  }, [topFour]);
  const handleShare = async () => {
    const yearningNames = topThreeYearnings.map(y => y.title).join(', ');
    const shareText = `My top yearnings are: ${yearningNames}. Discover yours at LiveWright!`;
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My LiveWright Yearnings',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled — do nothing
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        setShareLabel('Copied to clipboard!');
        setTimeout(() => setShareLabel('Share Your Top Yearnings'), 2500);
      } catch {
        // Fallback: do nothing
      }
    }
  };
  if (!isVisible) return null;
  const handleScrollToChat = () => {
    document.getElementById('chatbot')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="results" className="bg-gray-50 pt-3 pb-12 md:pt-10 md:pb-16 px-4 md:px-8 flex flex-col justify-center" data-id="element-147">
      <div className="max-w-3xl mx-auto w-full" ref={ref} data-id="element-148">
        <AnimatePresence mode="wait" data-id="element-149">
          {isAnalyzing ? <motion.div key="analyzing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4
        }} className="flex flex-col items-center justify-center min-h-[150px] space-y-4" data-id="element-150">
              <div className="w-16 h-16 rounded-full border-4 border-teal/30 border-t-teal pulse-spinner" data-id="element-151" />
              <p className="text-navy text-xl font-medium" data-id="element-152">
                Analyzing your selections...
              </p>
              <p className="text-gray-400 text-sm" data-id="element-153">
                Discovering what matters most to you
              </p>
            </motion.div> : <motion.div key="results" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6
        }} data-id="element-154">
              {/* Warm headline */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-center mb-3 md:mb-5" data-id="element-155">
                <h2 className="text-navy text-2xl md:text-4xl font-bold mb-1 md:mb-3" data-id="element-156">
                  Here's What Your Choices Reveal
                </h2>
                <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto" data-id="element-157">
                  Based on the images that resonated with you, these are the
                  needs most active in your life right now.
                </p>
              </motion.div>

              {/* Horizontal bar chart — top 4 needs */}
              <div className="space-y-5 mb-10" data-id="element-158">
                {topFour.map((item, idx) => {
              const yearning = YEARNINGS_MAP[item.need];
              const label = yearning ? yearning.title : `To ${item.need}`;
              return <motion.div key={item.need} initial={{
                opacity: 0,
                x: -30
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 0.2 + idx * 0.12
              }} data-id="element-159">
                      <div className="flex items-center justify-between mb-2" data-id="element-160">
                        <span className="text-navy font-bold text-base" data-id="element-161">
                          {label}
                        </span>
                        <span className="text-gray-500 font-medium text-sm" data-id="element-162">
                          {item.value}%
                        </span>
                      </div>
                      <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden" data-id="element-163">
                        <motion.div className="h-full rounded-full" style={{
                    background: 'linear-gradient(90deg, #0A2540, #00B2C6)'
                  }} initial={{
                    width: 0
                  }} animate={{
                    width: `${item.value}%`
                  }} transition={{
                    duration: 0.8,
                    delay: 0.3 + idx * 0.12,
                    ease: 'easeOut'
                  }} data-id="element-164" />
                      </div>
                    </motion.div>;
            })}
              </div>

              {/* Insight cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6" data-id="element-165">
                {topThreeYearnings.map((yearning, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.8 + idx * 0.1
            }} className="bg-white rounded-xl shadow-md p-6 border-t-4 border-teal" data-id="element-166">
                    <h3 className="text-teal text-lg font-bold mb-3" data-id="element-167">
                      {yearning.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-168">
                      {yearning.description}
                    </p>
                  </motion.div>)}
              </div>

              {/* Share button */}
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 1.1
          }} className="flex justify-center mb-10" data-id="element-169">
                <button onClick={handleShare} className="inline-flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-full font-semibold text-sm shadow-md hover:bg-navy/90 active:scale-[0.97] transition-all" data-id="element-170">
                  <ShareIcon className="w-4 h-4" data-id="element-171" />
                  {shareLabel}
                </button>
              </motion.div>

              {/* Scroll to chat CTA */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 1.2
          }} className="text-center flex flex-col items-center cursor-pointer group" onClick={handleScrollToChat} data-id="element-172">
                <p className="text-gray-500 mb-2 group-hover:text-teal transition-colors" data-id="element-173">
                  Want to explore what this means for you?
                </p>
                <motion.div animate={{
              y: [0, 8, 0]
            }} transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: 'easeInOut'
            }} data-id="element-174">
                  <ChevronDownIcon className="w-6 h-6 text-gray-400 group-hover:text-teal transition-colors" data-id="element-175" />
                </motion.div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}